import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Keep decoded media and native slideshow/zoom state when only the SKU's featured image changes.
        const mediaIds = this.dataset.mediaIds?.split(',') ?? [];
        const newMediaIds = newMediaGallery.dataset.mediaIds?.split(',') ?? [];
        if (
          this.dataset.productId === newMediaGallery.dataset.productId &&
          mediaIds.length > 0 &&
          mediaIds.length === newMediaIds.length &&
          newMediaIds.every((id) => mediaIds.includes(id))
        ) {
          const featuredId = newMediaGallery.dataset.featuredMediaId;
          if (featuredId !== this.dataset.featuredMediaId) {
            this.dataset.featuredMediaId = featuredId;
            const index = mediaIds.indexOf(featuredId);
            if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
          }
          return;
        }

        // With "hide other variants", only the first slide/thumbnail changes; retain the common media.
        if (
          this.dataset.productId === newMediaGallery.dataset.productId &&
          mediaIds.length > 0 &&
          mediaIds.length === newMediaIds.length &&
          mediaIds.slice(1).every((id, index) => id === newMediaIds[index + 1])
        ) {
          morph(this, newMediaGallery, {
            ...MORPH_OPTIONS,
            onBeforeUpdate(oldNode, newNode) {
              MORPH_OPTIONS.onBeforeUpdate(oldNode, newNode);
              if (oldNode instanceof Element && newNode instanceof Element && oldNode.hasAttribute('in-viewport')) {
                newNode.setAttribute('in-viewport', '');
              }
            },
          });
          this.dataset.mediaIds = newMediaGallery.dataset.mediaIds;
          this.dataset.featuredMediaId = newMediaGallery.dataset.featuredMediaId;
          queueMicrotask(() => this.slideshow?.select(0, undefined, { animate: false }));
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
